import { useQuery } from "@tanstack/react-query";

// Supported currencies for travel
export const SUPPORTED_CURRENCIES = [
  { code: "USD", name: "US Dollar", symbol: "$" },
  { code: "EUR", name: "Euro", symbol: "\u20AC" },
  { code: "GBP", name: "British Pound", symbol: "\u00A3" },
  { code: "CAD", name: "Canadian Dollar", symbol: "C$" },
  { code: "AUD", name: "Australian Dollar", symbol: "A$" },
  { code: "JPY", name: "Japanese Yen", symbol: "\u00A5" },
  { code: "CHF", name: "Swiss Franc", symbol: "CHF" },
  { code: "CNY", name: "Chinese Yuan", symbol: "\u00A5" },
  { code: "MXN", name: "Mexican Peso", symbol: "MX$" },
  { code: "BRL", name: "Brazilian Real", symbol: "R$" },
  { code: "INR", name: "Indian Rupee", symbol: "\u20B9" },
  { code: "SGD", name: "Singapore Dollar", symbol: "S$" },
  { code: "HKD", name: "Hong Kong Dollar", symbol: "HK$" },
  { code: "THB", name: "Thai Baht", symbol: "\u0E3F" },
  { code: "NZD", name: "New Zealand Dollar", symbol: "NZ$" },
  { code: "KRW", name: "South Korean Won", symbol: "\u20A9" },
  { code: "AED", name: "UAE Dirham", symbol: "AED" },
] as const;

export type CurrencyCode = (typeof SUPPORTED_CURRENCIES)[number]["code"];

export interface ExchangeRates {
  base: string;
  rates: Record<string, number>;
  fetchedAt: string;
}

// Fetch exchange rates from authenticated API
const EXCHANGE_RATE_API_KEY = "f887c242d83e8a2dc445712e";

async function fetchExchangeRatesFromApi(baseCurrency: string = "USD"): Promise<ExchangeRates> {
  // Use the authenticated v6 API endpoint
  const response = await fetch(
    `https://v6.exchangerate-api.com/v6/${EXCHANGE_RATE_API_KEY}/latest/${baseCurrency}`
  );

  if (!response.ok) {
    throw new Error("Failed to fetch exchange rates");
  }

  const data = await response.json();

  // v6 API returns { result: "success", base_code: "USD", conversion_rates: {...} }
  if (data.result !== "success") {
    throw new Error(data["error-type"] || "Exchange rate API error");
  }

  return {
    base: data.base_code,
    rates: data.conversion_rates,
    fetchedAt: new Date().toISOString(),
  };
}

// React Query hook for exchange rates
export function useExchangeRates(baseCurrency: string = "USD") {
  return useQuery({
    queryKey: ["exchangeRates", baseCurrency],
    queryFn: () => fetchExchangeRatesFromApi(baseCurrency),
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 24 * 60 * 60 * 1000, // 24 hours
    retry: 2,
  });
}

// Convert amount between currencies
export function convertCurrency(
  amount: number,
  fromCurrency: string,
  toCurrency: string,
  rates: ExchangeRates
): number {
  if (fromCurrency === toCurrency) {
    return amount;
  }

  // If rates are based on the target currency, simple division
  if (rates.base === toCurrency) {
    const fromRate = rates.rates[fromCurrency];
    if (!fromRate) return amount;
    return amount / fromRate;
  }

  // If rates are based on the source currency, simple multiplication
  if (rates.base === fromCurrency) {
    const toRate = rates.rates[toCurrency];
    if (!toRate) return amount;
    return amount * toRate;
  }

  // Cross conversion: convert from source to base, then base to target
  const fromRate = rates.rates[fromCurrency];
  const toRate = rates.rates[toCurrency];

  if (!fromRate || !toRate) return amount;

  // Convert to base currency first, then to target
  const inBase = amount / fromRate;
  return inBase * toRate;
}

// Format currency with symbol
export function formatCurrency(
  amount: number,
  currencyCode: string = "USD",
  options?: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  }
): string {
  const { minimumFractionDigits = 0, maximumFractionDigits = 2 } = options || {};

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(amount);
  } catch {
    // Fallback for unsupported currencies
    const currency = SUPPORTED_CURRENCIES.find((c) => c.code === currencyCode);
    const symbol = currency?.symbol || currencyCode;
    return `${symbol}${amount.toFixed(maximumFractionDigits)}`;
  }
}

// Get currency symbol
export function getCurrencySymbol(currencyCode: string): string {
  const currency = SUPPORTED_CURRENCIES.find((c) => c.code === currencyCode);
  return currency?.symbol || currencyCode;
}

// Calculate cents per point (CPP)
export function calculateCPP(
  pointsUsed: number,
  retailPrice: number,
  cashPaid: number = 0,
  taxesFeesPaid: number = 0
): number | null {
  if (!pointsUsed || pointsUsed === 0) return null;
  if (!retailPrice || retailPrice === 0) return null;

  const totalCashPaid = cashPaid + taxesFeesPaid;
  const valueReceived = retailPrice - totalCashPaid;

  if (valueReceived <= 0) return null;

  // Return cents per point
  return (valueReceived / pointsUsed) * 100;
}

// Format CPP with color indicator
export function formatCPP(cpp: number | null): { value: string; color: string } {
  if (cpp === null) {
    return { value: "N/A", color: "text-muted-foreground" };
  }

  const formatted = cpp.toFixed(2) + " cpp";

  // Color coding based on general point valuations
  // >= 1.5 cpp is good value
  // 1.0 - 1.5 cpp is decent
  // < 1.0 cpp is poor value
  if (cpp >= 1.5) {
    return { value: formatted, color: "text-green-600" };
  } else if (cpp >= 1.0) {
    return { value: formatted, color: "text-yellow-600" };
  } else {
    return { value: formatted, color: "text-red-600" };
  }
}

// Common loyalty programs
export const LOYALTY_PROGRAMS = [
  // Airlines
  { name: "United MileagePlus", type: "airline" },
  { name: "Delta SkyMiles", type: "airline" },
  { name: "American AAdvantage", type: "airline" },
  { name: "Southwest Rapid Rewards", type: "airline" },
  { name: "JetBlue TrueBlue", type: "airline" },
  { name: "Alaska Mileage Plan", type: "airline" },
  { name: "British Airways Avios", type: "airline" },
  { name: "Air France-KLM Flying Blue", type: "airline" },
  { name: "Lufthansa Miles & More", type: "airline" },
  { name: "Singapore KrisFlyer", type: "airline" },
  { name: "Cathay Asia Miles", type: "airline" },
  { name: "Qantas Frequent Flyer", type: "airline" },
  { name: "Emirates Skywards", type: "airline" },
  // Hotels
  { name: "Marriott Bonvoy", type: "hotel" },
  { name: "Hilton Honors", type: "hotel" },
  { name: "IHG One Rewards", type: "hotel" },
  { name: "World of Hyatt", type: "hotel" },
  { name: "Wyndham Rewards", type: "hotel" },
  { name: "Choice Privileges", type: "hotel" },
  { name: "Accor Live Limitless", type: "hotel" },
  // Credit Card / Transferable
  { name: "Chase Ultimate Rewards", type: "bank" },
  { name: "Amex Membership Rewards", type: "bank" },
  { name: "Citi ThankYou Points", type: "bank" },
  { name: "Capital One Miles", type: "bank" },
  { name: "Bilt Rewards", type: "bank" },
] as const;

export type LoyaltyProgram = (typeof LOYALTY_PROGRAMS)[number]["name"];
